import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EntityLinkSelector } from '@/components/shared/EntityLinkSelector';
import type { EntityType } from '@/types/link';

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface RiskCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: any, links?: SelectedEntity[]) => void;
  isCreating?: boolean;
}

export function RiskCreateDialog({ open, onOpenChange, onSave, isCreating }: RiskCreateDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    asset_or_process: '',
    cause: '',
    consequence: '',
    impact: 3,
    likelihood: 3,
    status: 'identified',
    notes: '',
  });
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);

  const handleSubmit = () => {
    onSave(formData, selectedLinks);
    setFormData({
      title: '',
      asset_or_process: '',
      cause: '',
      consequence: '',
      impact: 3,
      likelihood: 3,
      status: 'identified',
      notes: '',
    });
    setSelectedLinks([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Add New Risk</DialogTitle>
          <DialogDescription>
            Identify and assess a new organizational risk.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">Risk Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Data breach through supplier system"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="asset_or_process">Asset or Process</Label>
            <Input
              id="asset_or_process"
              value={formData.asset_or_process}
              onChange={(e) => setFormData({ ...formData, asset_or_process: e.target.value })}
              placeholder="e.g., CRM System"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="cause">Cause</Label>
            <Textarea
              id="cause"
              value={formData.cause}
              onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
              placeholder="What could cause this risk?"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="consequence">Consequence</Label>
            <Textarea
              id="consequence"
              value={formData.consequence}
              onChange={(e) => setFormData({ ...formData, consequence: e.target.value })}
              placeholder="What would happen if this risk occurs?"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="impact">Impact (1-5)</Label>
              <Select
                value={formData.impact.toString()}
                onValueChange={(value) => setFormData({ ...formData, impact: parseInt(value) })}
              >
                <SelectTrigger id="impact">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Low</SelectItem>
                  <SelectItem value="2">2 - Low</SelectItem>
                  <SelectItem value="3">3 - Medium</SelectItem>
                  <SelectItem value="4">4 - High</SelectItem>
                  <SelectItem value="5">5 - Very High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="likelihood">Likelihood (1-5)</Label>
              <Select
                value={formData.likelihood.toString()}
                onValueChange={(value) => setFormData({ ...formData, likelihood: parseInt(value) })}
              >
                <SelectTrigger id="likelihood">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 - Very Unlikely</SelectItem>
                  <SelectItem value="2">2 - Unlikely</SelectItem>
                  <SelectItem value="3">3 - Possible</SelectItem>
                  <SelectItem value="4">4 - Likely</SelectItem>
                  <SelectItem value="5">5 - Very Likely</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-2">
            <Label>Risk Score</Label>
            <div className="text-2xl font-bold">
              {formData.impact * formData.likelihood}
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="identified">Identified</SelectItem>
                <SelectItem value="assessing">Assessing</SelectItem>
                <SelectItem value="treating">Treating</SelectItem>
                <SelectItem value="monitoring">Monitoring</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional information..."
            />
          </div>
        </div>

        <EntityLinkSelector
          allowedTypes={['asset', 'system', 'control', 'evidence', 'supplier', 'incident', 'task']}
          onLinksChange={setSelectedLinks}
          initialLinks={selectedLinks}
        />
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={!formData.title || isCreating}
          >
            {isCreating ? 'Creating...' : 'Create Risk'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
